// Widget registry — central catalogue of all dashboard widget types.
// Adding a new widget means adding one entry here. Each entry knows its
// own component, default grid size and a human label for the picker.
//
// Widgets must render with `h-full w-full` and gracefully scale into any
// container ≥ their declared minSize. WidgetShell (below) handles the
// "container is below minimum" case automatically by rendering a friendly
// placeholder when the rendered DOM falls under a per-widget pixel threshold.

import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  Activity,
  BarChart3,
  Bookmark,
  Briefcase,
  Calendar as CalendarIcon,
  CalendarOff,
  ChevronDown,
  ChevronRight,
  Cloud,
  Code,
  Database as DatabaseIcon,
  DollarSign,
  Download,
  FileText,
  Flame,
  Folder,
  Gauge,
  GitBranch,
  Globe,
  GraduationCap,
  Image as ImageIcon,
  KanbanSquare,
  ListChecks,
  Lock,
  type LucideIcon,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Plus,
  Receipt,
  Server,
  Settings as SettingsIcon,
  ShoppingCart,
  Sparkles,
  Stethoscope,
  Trash2,
  TrendingUp,
  Upload,
  Users as UsersIcon,
  Video,
  X,
} from 'lucide-react'
import { type ComponentType, type ReactNode, useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  type CountAbsenceDaysOptions,
  countAbsenceDays,
  localTodayISO,
} from '../../../../../packages/shared/src/saldo'
import type { Absence, AbsenceType } from '../../lib/api'
import { api } from '../../lib/api'
import { hasBudgetData } from '../../lib/budget-view'
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from '../../lib/calendar'
import {
  targetMinutesForDay,
  useHolidaysInRange,
  useWorkSchedulesForUser,
} from '../../lib/calendar-overlays'
import { useThemeColors } from '../../lib/theme-colors'
import { useBudgetProjects } from '../../lib/use-budget-projects'
import { cn, formatDuration } from '../../lib/utils'
import { AbsenceFormDialog } from '../absence-form-dialog'
import { AuswertungPageBody } from '../auswertung-panel'
import { BudgetProjectSelect } from '../budget/budget-project-select'
import { BudgetPeriodLabel, BudgetRates, BudgetSummary } from '../budget/budget-summary'
import { Button } from '../ui/button'
import { Dialog } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'

export type WidgetType =
  | 'my-tasks'
  | 'weekly-soll-ist'
  | 'web-shortcut'
  | 'absence-overview'
  | 'vacation-balance'
  | 'project-budget'
  | 'tax-deadlines'
  | 'performance-overview'
  | 'team-status'
  | 'range-report'

export interface WidgetRenderProps {
  /** Per-instance config — opaque to the grid, owned by the widget. */
  config: Record<string, unknown>
  /** Persist a new config blob for this instance. */
  onConfigChange: (next: Record<string, unknown>) => void
  /** Whether the dashboard is in edit-mode (drag/resize/configure). */
  editMode: boolean
  /** Remove this widget instance — provided by the grid. Chromeless widgets
   * render their own remove affordance and call this; framed widgets can
   * ignore it (the grid renders a trash icon in the header). */
  onRemove: () => void
}

export interface WidgetDef {
  type: WidgetType
  label: string
  description: string
  icon: ComponentType<{ className?: string }>
  defaultSize: { w: number; h: number }
  minSize: { w: number; h: number }
  /** Pixel threshold for the soft "too small" gate (currently unused
   * passthrough; kept on the type for later). */
  minPixels: { width: number; height: number }
  /** When true, dashboard renders the widget without the standard card
   * chrome (border, header, padding) — the widget is the tile itself.
   * The widget is responsible for its own drag-handle, edit and remove
   * affordances when chromeless. */
  chromeless?: boolean
  /** When false, the widget cannot be resized — the bottom-right resize
   * handle is hidden and the size is locked to defaultSize. */
  resizable?: boolean
  Render: ComponentType<WidgetRenderProps>
}

/** Wrapper around every widget — used to be a size-gate, but currently just
 * a passthrough so we can still see what the widget does at every size. */
export function WidgetShell({
  minPixels: _minPixels,
  children,
}: {
  minPixels: { width: number; height: number }
  children: ReactNode
}) {
  return <div className="h-full w-full flex flex-col overflow-hidden">{children}</div>
}

function WeeklySollIstWidget(_: WidgetRenderProps) {
  const { data: weekly } = useQuery({
    queryKey: ['reports', 'weekly'],
    queryFn: () => api.weeklyReport(),
  })
  const colors = useThemeColors()
  const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']

  if (!weekly) return <p className="text-sm text-text-tertiary p-4">Lade …</p>

  const chartData = weekly.days.map((d) => ({
    name: dayNames[d.weekday],
    actual: +(d.actualMs / 3_600_000).toFixed(2),
    target: d.targetHours,
    date: d.date,
  }))
  const targetMs = weekly.weekTargetHours * 60 * 60 * 1000
  const pct = targetMs > 0 ? Math.min(100, Math.round((weekly.weekActualMs / targetMs) * 100)) : 0

  return (
    <div className="h-full flex flex-col gap-3 min-h-0">
      {/* Chart fills remaining height; min-h-0 lets flex shrink it. */}
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.gridLine} />
            <XAxis
              dataKey="name"
              tick={{ fill: colors.textSecondary, fontSize: 11 }}
              axisLine={{ stroke: colors.gridLine }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: colors.textTertiary, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              unit="h"
            />
            <Tooltip
              cursor={{ fill: colors.accentMuted }}
              contentStyle={{
                backgroundColor: colors.tooltipBg,
                border: `1px solid ${colors.tooltipBorder}`,
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: colors.textPrimary }}
              itemStyle={{ color: colors.accent }}
              formatter={(value, name) => [
                `${Number(value)}h`,
                String(name) === 'actual' ? 'Ist' : 'Soll',
              ]}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, color: colors.textSecondary }}
              formatter={(v) => (v === 'actual' ? 'Ist' : 'Soll')}
            />
            <Bar
              dataKey="target"
              fill={colors.textTertiary}
              fillOpacity={0.25}
              radius={[4, 4, 0, 0]}
            />
            <Bar dataKey="actual" fill={colors.accent} radius={[4, 4, 0, 0]}>
              {chartData.map((d) => (
                <Cell
                  key={d.date}
                  fill={d.actual >= d.target && d.target > 0 ? '#10B981' : colors.accent}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Summary row stays compact. */}
      <div className="flex items-baseline justify-between border-t border-border-subtle pt-2 shrink-0 text-sm">
        <div>
          <p className="text-2xs uppercase tracking-wider text-text-tertiary">Σ Ist</p>
          <p className="font-mono text-lg font-light text-accent tabular-nums">
            {formatDuration(weekly.weekActualMs)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xs uppercase tracking-wider text-text-tertiary">Soll</p>
          <p className="font-mono text-lg font-light tabular-nums text-text-secondary">
            {weekly.weekTargetHours}h
          </p>
          {weekly.weekTargetHours > 0 && (
            <p className="text-2xs text-text-tertiary">{pct}% erreicht</p>
          )}
        </div>
      </div>
    </div>
  )
}

// === Web-Shortcut ============================================================
// "Webclip" tile: paste a URL, get an app-style tile. Icon comes from one of:
//   - 'favicon' (default): Google S2 favicons API for the URL's domain
//   - 'preset':  one of the curated business-area icons defined below
//   - 'custom':  user-uploaded image stored as data: URL in config
// Editing happens in a modal Dialog so URL/label/icon all have proper room.

interface WebShortcutConfig {
  url?: string
  label?: string
  iconKind?: 'favicon' | 'preset' | 'custom'
  presetName?: string
  customDataUrl?: string
  /** When iconKind === 'favicon', the user can pick a specific candidate
   * via the dialog picker. If absent, the widget falls back to the
   * server's auto-pick. bgColor is stored alongside so the tile renders
   * without an extra round-trip on every load. */
  faviconUrl?: string
  faviconBgColor?: string | null
}

interface IconPreset {
  name: string
  label: string
  icon: LucideIcon
  /** Background tint for the tile box — small dose of color goes a long way. */
  tint: string
}

const ICON_PRESETS: IconPreset[] = [
  { name: 'globe', label: 'Allgemein', icon: Globe, tint: '#94a3b8' },
  { name: 'briefcase', label: 'Business', icon: Briefcase, tint: '#a16207' },
  { name: 'mail', label: 'E-Mail', icon: Mail, tint: '#0ea5e9' },
  { name: 'chat', label: 'Kommunikation', icon: MessageSquare, tint: '#10b981' },
  { name: 'docs', label: 'Dokumente', icon: FileText, tint: '#6366f1' },
  { name: 'folder', label: 'Dateien', icon: Folder, tint: '#eab308' },
  { name: 'cloud', label: 'Cloud', icon: Cloud, tint: '#38bdf8' },
  { name: 'calendar', label: 'Kalender', icon: CalendarIcon, tint: '#f97316' },
  { name: 'kanban', label: 'Projekt-Board', icon: KanbanSquare, tint: '#a855f7' },
  { name: 'code', label: 'Entwicklung', icon: Code, tint: '#22d3ee' },
  { name: 'github', label: 'Code-Hosting / Git', icon: GitBranch, tint: '#1e293b' },
  { name: 'database', label: 'Datenbank', icon: DatabaseIcon, tint: '#0891b2' },
  { name: 'server', label: 'Server / Infra', icon: Server, tint: '#475569' },
  { name: 'analytics', label: 'Analytics', icon: BarChart3, tint: '#16a34a' },
  { name: 'finance', label: 'Finanzen', icon: DollarSign, tint: '#059669' },
  { name: 'invoice', label: 'Buchhaltung', icon: Receipt, tint: '#ca8a04' },
  { name: 'shop', label: 'Shop / E-Commerce', icon: ShoppingCart, tint: '#db2777' },
  { name: 'video', label: 'Videokonferenz', icon: Video, tint: '#dc2626' },
  { name: 'security', label: 'Security / Auth', icon: Lock, tint: '#475569' },
  { name: 'crm', label: 'CRM / Kundenbeziehung', icon: UsersIcon, tint: '#0d9488' },
  { name: 'design', label: 'Design / Bilder', icon: ImageIcon, tint: '#e11d48' },
  { name: 'admin', label: 'Admin', icon: SettingsIcon, tint: '#6b7280' },
  { name: 'bookmark', label: 'Bookmark', icon: Bookmark, tint: '#f59e0b' },
]

function presetByName(name: string | undefined): IconPreset | null {
  if (!name) return null
  return ICON_PRESETS.find((p) => p.name === name) ?? null
}

function normalizeUrl(input: string): string {
  const trimmed = input.trim()
  if (!trimmed) return ''
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

const MAX_CUSTOM_ICON_BYTES = 64 * 1024 // 64 KB safety limit

// Tile-background palette for two cases:
//   1. Favicon present → tint derived from extractDominantColor (canvas
//      sampling, see useDominantColor). If CORS blocks the read, we
//      fall back to a deterministic palette pick.
//   2. Favicon missing/broken → ember-flame in a deterministic palette
//      pick. Same domain → same color, every time, so the user builds
//      muscle memory ("the purple one is GitLab, the green one is
//      Mailcow").
const TILE_PALETTE = [
  '#ef4444', // red
  '#f97316', // orange
  '#f59e0b', // amber
  '#eab308', // yellow
  '#84cc16', // lime
  '#10b981', // emerald
  '#14b8a6', // teal
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#a855f7', // purple
  '#d946ef', // fuchsia
  '#ec4899', // pink
  '#f43f5e', // rose
]

function deriveTileColor(url: string): string {
  try {
    const host = new URL(url).hostname
    // djb2-style hash → stable across reloads, doesn't collide as much
    // as naive char-sum
    let h = 5381
    for (let i = 0; i < host.length; i++) h = ((h * 33) ^ host.charCodeAt(i)) >>> 0
    return TILE_PALETTE[h % TILE_PALETTE.length]
  } catch {
    return TILE_PALETTE[0]
  }
}

/** Asks the backend to resolve the best icon for a page URL.
 *
 * Backend logic:
 *   1. Fetches the page HTML, parses <link rel="icon"> / apple-touch-
 *      icon, picks the largest declared size
 *   2. Falls back to convention paths (/apple-touch-icon.png,
 *      /favicon.ico, etc.) when the site doesn't declare anything
 *   3. Decodes the winning image with sharp, samples corner pixels
 *      to estimate the icon's own background color
 *
 * The frontend uses `bgColor` to fill the tile so the favicon and
 * its surrounding rounded-square form a single uniform shape. When
 * resolution fails entirely (`iconUrl=null`), we fall back to the
 * ember-flame placeholder in a deterministic palette color. */
function useResolvedIcon(url: string): {
  iconUrl: string | null
  bgColor: string | null
  loaded: 'idle' | 'loading' | 'ok' | 'failed'
} {
  const { data, isFetching, isError } = useQuery({
    queryKey: ['icon-meta', url],
    queryFn: () => (url ? api.fetchIconMeta(url) : null),
    enabled: !!url,
    // Backend caches 1h already; staleTime here gates re-fetches when
    // the same widget re-renders. Garbage-collect after a day so we
    // re-check eventually (favicons can change with deploys).
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: false,
  })
  if (!url) return { iconUrl: null, bgColor: null, loaded: 'idle' }
  if (isFetching && !data) return { iconUrl: null, bgColor: null, loaded: 'loading' }
  if (isError) return { iconUrl: null, bgColor: null, loaded: 'failed' }
  if (!data) return { iconUrl: null, bgColor: null, loaded: 'loading' }
  if (!data.iconUrl) return { iconUrl: null, bgColor: null, loaded: 'failed' }
  return { iconUrl: data.iconUrl, bgColor: data.bgColor, loaded: 'ok' }
}

/** Server-samples the bgColor for a manually-picked favicon URL whose
 * stored bgColor is null (older picks, or transparent-canvas logos
 * the server can decode now but couldn't before). Lazy — only runs
 * when needed. Cached per icon-URL, gc'd after 24h. */
function useRefreshedBgColor(iconUrl: string | null): string | null {
  const { data } = useQuery({
    queryKey: ['icon-color', iconUrl],
    queryFn: () => (iconUrl ? api.fetchIconColor(iconUrl) : Promise.resolve({ bgColor: null })),
    enabled: !!iconUrl,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: false,
  })
  return data?.bgColor ?? null
}

/** Picks a subtle border color for a tile whose bg is the extracted
 * icon-bg. Slight darken for light backgrounds, slight lighten for
 * dark backgrounds — keeps the tile crisp without a hard outline. */
function deriveBorderFromBg(bg: string): string {
  const m = bg.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
  if (!m) return 'rgba(0, 0, 0, 0.2)'
  const r = parseInt(m[1], 10)
  const g = parseInt(m[2], 10)
  const b = parseInt(m[3], 10)
  // Perceived brightness — Rec.601 luma.
  const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  const shift = luma > 0.6 ? -28 : 18
  const clamp = (v: number) => Math.max(0, Math.min(255, v + shift))
  return `rgb(${clamp(r)}, ${clamp(g)}, ${clamp(b)})`
}

/** Converts `rgb(r, g, b)` → `rgba(r, g, b, alpha)` for tile tinting.
 * Pass-through for already-formatted rgba or hex (with alpha appended
 * for hex). Used so the tile background is a SOFT version of whatever
 * color we picked — full strength on the icon, low-alpha behind it. */
function withAlpha(color: string, alpha: number): string {
  if (color.startsWith('rgb(')) {
    return color.replace(/^rgb\(/, 'rgba(').replace(/\)$/, `, ${alpha})`)
  }
  if (color.startsWith('#')) {
    // 8-digit hex form: append alpha byte
    const a = Math.round(alpha * 255)
      .toString(16)
      .padStart(2, '0')
    return `${color}${a}`
  }
  return color
}

/** Tile that renders the icon based on the active iconKind. Used both inside
 * the widget and in the edit dialog as a live preview.
 *
 * Layout strategy:
 *   - No label  → icon is perfectly centred (the inner square's geometric mid).
 *   - With label → grid with `1fr auto`: the icon area takes remaining space
 *     centred within itself, label sits as a fixed strip at the bottom. The
 *     visual effect is the icon shifts slightly upwards relative to the
 *     square's centre, leaving breathing room for the label below.
 */
function ShortcutTileVisual({
  cfg,
  iconBroken: _iconBroken,
  onIconError: _onIconError,
}: {
  cfg: WebShortcutConfig
  iconBroken: boolean
  onIconError: () => void
}) {
  const url = cfg.url ?? ''
  const label = (cfg.label ?? '').trim()
  const kind = cfg.iconKind ?? 'favicon'
  const preset = kind === 'preset' ? presetByName(cfg.presetName) : null
  const hasLabel = label.length > 0

  // Resolve the favicon. If the user picked a specific candidate via the
  // dialog, use that directly (no server round-trip needed). Otherwise
  // ask the backend for the auto-pick. No-op when kind !== 'favicon'.
  const userPicked = kind === 'favicon' && cfg.faviconUrl ? cfg.faviconUrl : null
  const resolverInput = kind === 'favicon' && !userPicked ? url : ''
  const auto = useResolvedIcon(resolverInput)
  // For user-picked icons whose bgColor was frozen as null (picked before
  // BMP-ICO decoding existed, or transparent-canvas logos), refresh the
  // colour from the backend so the tile still reads "wie aus einem Guss"
  // without forcing the user to re-pick.
  const needsColorRefresh =
    userPicked != null && !cfg.faviconBgColor && userPicked.startsWith('http')
  const refreshedBg = useRefreshedBgColor(needsColorRefresh ? userPicked : null)
  const iconUrl = userPicked ?? auto.iconUrl
  const bgColor = userPicked ? (cfg.faviconBgColor ?? refreshedBg) : auto.bgColor
  const loaded: 'idle' | 'loading' | 'ok' | 'failed' = userPicked ? 'ok' : auto.loaded
  const fallbackColor = useMemo(() => deriveTileColor(url), [url])

  // Consistent icon sizing: 40x40 box, 32x32 inner image/svg.
  // Fixed dimensions prevent distortion at small cell widths and
  // keep tiles visually uniform regardless of favicon source quality.
  const boxClass = 'h-14 w-14'
  const innerImg = 'h-12 w-12'
  const innerSvg = 'h-9 w-9'

  let body: ReactNode = null

  if (kind === 'custom' && cfg.customDataUrl) {
    // ── Custom uploaded image — keep its own surface (neutral box).
    body = (
      <div
        className={`flex items-center justify-center ${boxClass} rounded-2xl bg-bg-overlay/60 border border-border-subtle overflow-hidden shadow-sm shrink-0`}
      >
        <img src={cfg.customDataUrl} alt="" className={`${innerImg} object-contain`} />
      </div>
    )
  } else if (preset) {
    // ── Curated preset — tint box with the preset's defined color.
    const Icon = preset.icon
    body = (
      <div
        className={`flex items-center justify-center ${boxClass} rounded-2xl border overflow-hidden shadow-sm shrink-0`}
        style={{ backgroundColor: `${preset.tint}1a`, borderColor: `${preset.tint}40` }}
      >
        <Icon className={innerSvg} style={{ color: preset.tint }} />
      </div>
    )
  } else if (kind === 'favicon' && !url) {
    // ── Empty widget — Globe in neutral tone, signals "fill me with a URL".
    body = (
      <div
        className={`flex items-center justify-center ${boxClass} rounded-2xl border border-dashed border-border-default bg-bg-overlay/30 overflow-hidden shrink-0`}
      >
        <Globe className={`${innerSvg} text-text-tertiary`} strokeWidth={1.75} />
      </div>
    )
  } else if (kind === 'favicon' && url && loaded === 'ok' && iconUrl) {
    // ── Favicon resolved successfully. Fill the entire app-tile-square
    // with the icon's own background color so favicon + surface read as
    // one piece. Falls back to a light tint when the icon is fully
    // transparent (corner pixels alpha < threshold → bgColor = null).
    const tileBg = bgColor ?? withAlpha(fallbackColor, 0.15)
    const borderColor = bgColor ? deriveBorderFromBg(bgColor) : withAlpha(fallbackColor, 0.4)
    body = (
      <div
        className={`flex items-center justify-center ${boxClass} rounded-2xl border overflow-hidden shadow-sm shrink-0`}
        style={{ backgroundColor: tileBg, borderColor }}
      >
        <img src={iconUrl} alt="" className={`${innerImg} object-contain`} />
      </div>
    )
  } else if (kind === 'favicon' && loaded === 'loading') {
    // ── Still resolving — soft empty placeholder. No spinner since
    // resolution usually completes < 1s and a flash is more jarring
    // than a still empty tile.
    body = (
      <div
        className={`${boxClass} rounded-2xl bg-bg-overlay/40 border border-border-subtle overflow-hidden shrink-0`}
      />
    )
  } else {
    // ── Favicon resolution failed entirely. Show ember Flame in the
    // deterministic per-URL palette color — same app-square footprint,
    // tinted box. This is the "tried our best, no real icon exists"
    // case, distinguished from the empty-widget Globe above.
    body = (
      <div
        className={`flex items-center justify-center ${boxClass} rounded-2xl border overflow-hidden shadow-sm shrink-0`}
        style={{
          backgroundColor: withAlpha(fallbackColor, 0.15),
          borderColor: withAlpha(fallbackColor, 0.4),
        }}
      >
        <Flame className={innerSvg} style={{ color: fallbackColor }} strokeWidth={2} />
      </div>
    )
  }

  return (
    <div className="absolute inset-0">
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={hasLabel ? { paddingBottom: '22%' } : undefined}
      >
        {body}
      </div>
      {hasLabel && (
        <div className="absolute inset-x-0 bottom-1 px-1.5 min-w-0">
          <span className="block text-2xs leading-tight text-center text-text-secondary truncate">
            {label}
          </span>
        </div>
      )}
    </div>
  )
}

function WebShortcutWidget({ config, onConfigChange, editMode, onRemove }: WidgetRenderProps) {
  const cfg = config as WebShortcutConfig
  // Dialog stays closed by default — even brand-new widgets show an empty
  // tile placeholder. User clicks the tile (or the pencil in edit-mode) to
  // configure. Avoids the page-load flood of auto-popups when a user has
  // multiple unconfigured widgets.
  const [dialogOpen, setDialogOpen] = useState(false)
  const [iconBroken, setIconBroken] = useState(false)

  // Reset broken-state if the user changes the URL or icon source.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setter is stable; we want the effect keyed by these primitive values
  useEffect(() => setIconBroken(false), [cfg.url, cfg.iconKind])

  const url = cfg.url ?? ''
  const TileNode = (
    <ShortcutTileVisual cfg={cfg} iconBroken={iconBroken} onIconError={() => setIconBroken(true)} />
  )

  // ONE rounded card fills the cell completely — the cell IS the tile.
  // No nested squares. Locked size in WidgetDef keeps the cell near 1:1.
  // In view-mode the outer card is invisible (no bg, no border) — just the
  // icon floats on the dashboard background. The frame returns in edit mode
  // so the tile's bounds are visible while arranging.
  const cardEdit =
    'relative h-full w-full rounded-2xl bg-bg-overlay/40 border border-border-subtle ring-1 ring-accent/40 overflow-hidden'
  const cardView = 'relative h-full w-full rounded-2xl overflow-hidden'

  return (
    <>
      <div className="relative h-full w-full">
        {editMode ? (
          <>
            <div className={`${cardEdit} pointer-events-none select-none`}>{TileNode}</div>
            {/* iOS-jiggle edit affordances at the card's outer corners. */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setDialogOpen(true)
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setDialogOpen(true)
              }}
              aria-label="Bearbeiten"
              className="pointer-events-auto absolute -top-1 -left-1 h-5 w-5 touch:h-9 touch:w-9 touch:-top-2 touch:-left-2 flex items-center justify-center rounded-full bg-bg-elevated text-text-secondary hover:text-accent shadow-md ring-1 ring-border-default z-20 touch:opacity-100"
            >
              <Pencil className="h-2.5 w-2.5 touch:h-4 touch:w-4" />
            </button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation()
                onRemove()
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => {
                e.stopPropagation()
                e.preventDefault()
                onRemove()
              }}
              aria-label="Verknüpfung entfernen"
              className="pointer-events-auto absolute -top-1 -right-1 h-5 w-5 touch:h-9 touch:w-9 touch:-top-2 touch:-right-2 rounded-full bg-bg-elevated hover:bg-bg-elevated text-text-tertiary hover:text-danger shadow-md ring-1 ring-border-default z-20 touch:opacity-100"
            >
              <X className="h-2.5 w-2.5 touch:h-4 touch:w-4" />
            </Button>
          </>
        ) : url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className={`group ${cardView} block transition-transform duration-300 ease-out will-change-transform hover:-translate-y-1 hover:scale-[1.04] active:scale-[0.97] active:duration-100 focus:outline-none focus-visible:outline-none`}
            style={{
              transformOrigin: 'center',
              WebkitTapHighlightColor: 'transparent',
              outline: 'none',
            }}
          >
            {TileNode}
          </a>
        ) : (
          <button type="button" onClick={() => setDialogOpen(true)} className={cardView}>
            {TileNode}
          </button>
        )}
      </div>

      {dialogOpen && (
        <ShortcutEditDialog
          initial={cfg}
          onClose={() => setDialogOpen(false)}
          onSave={(next) => {
            onConfigChange(next as Record<string, unknown>)
            setIconBroken(false)
            setDialogOpen(false)
          }}
          onRemove={() => {
            setDialogOpen(false)
            onRemove()
          }}
        />
      )}
    </>
  )
}

// === Favicon Picker ==========================================================

/** Grid of all icon candidates the backend found for the given page URL.
 * Renders each at app-tile scale with its own resolved bgColor so the
 * user can compare and pick. The "Automatisch" tile clears the manual
 * choice — the dashboard then falls back to the auto-pick. */
function FaviconPicker({
  pageUrl,
  selected,
  onSelect,
  onClear,
  fallbackColor,
}: {
  pageUrl: string
  selected: string | undefined
  onSelect: (iconUrl: string, bgColor: string | null) => void
  onClear: () => void
  fallbackColor: string
}) {
  const { data, isFetching } = useQuery({
    queryKey: ['icon-candidates', pageUrl],
    queryFn: () =>
      pageUrl ? api.fetchIconCandidates(pageUrl) : Promise.resolve({ candidates: [] }),
    enabled: !!pageUrl,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: false,
  })
  const candidates = data?.candidates ?? []
  const autoActive = !selected

  if (!pageUrl) {
    return (
      <div className="rounded-md border border-border-subtle bg-bg-overlay/30 p-3 text-xs text-text-tertiary">
        URL eintragen, um Favicon-Optionen zu sehen.
      </div>
    )
  }

  return (
    <div className="rounded-md border border-border-subtle bg-bg-overlay/30 p-2">
      <div className="grid grid-cols-4 gap-2">
        <button
          type="button"
          onClick={onClear}
          className={`flex flex-col items-center gap-1 rounded-md p-1.5 transition-colors ${
            autoActive ? 'bg-accent/15 ring-1 ring-accent/40' : 'hover:bg-bg-overlay/60'
          }`}
        >
          <div
            className="flex h-12 w-12 items-center justify-center rounded-2xl border border-border-subtle bg-bg-overlay/60"
            style={{ borderColor: withAlpha(fallbackColor, 0.4) }}
          >
            <Sparkles className="h-5 w-5" style={{ color: fallbackColor }} />
          </div>
          <span className="text-2xs text-text-secondary">Automatisch</span>
        </button>
        {candidates.map((c) => {
          const active = c.iconUrl === selected
          const tileBg = c.bgColor ?? withAlpha(fallbackColor, 0.15)
          const borderColor = c.bgColor
            ? deriveBorderFromBg(c.bgColor)
            : withAlpha(fallbackColor, 0.4)
          return (
            <button
              key={c.iconUrl}
              type="button"
              onClick={() => onSelect(c.iconUrl, c.bgColor)}
              className={`flex flex-col items-center gap-1 rounded-md p-1.5 transition-colors ${
                active ? 'bg-accent/15 ring-1 ring-accent/40' : 'hover:bg-bg-overlay/60'
              }`}
              title={c.iconUrl}
            >
              <div
                className="flex h-12 w-12 items-center justify-center rounded-2xl border overflow-hidden shadow-sm"
                style={{ backgroundColor: tileBg, borderColor }}
              >
                <img src={c.iconUrl} alt="" className="h-9 w-9 object-contain" />
              </div>
              <span className="text-2xs text-text-secondary truncate max-w-full px-0.5">
                {c.hint}
              </span>
            </button>
          )
        })}
      </div>
      {isFetching && candidates.length === 0 && (
        <div className="mt-2 text-center text-[11px] text-text-tertiary">
          Icons werden geladen …
        </div>
      )}
      {!isFetching && candidates.length === 0 && (
        <div className="mt-2 text-center text-[11px] text-text-tertiary">
          Keine Icons gefunden — die Kachel zeigt die Flamme als Fallback.
        </div>
      )}
    </div>
  )
}

// === Edit Dialog =============================================================

function ShortcutEditDialog({
  initial,
  onClose,
  onSave,
  onRemove,
}: {
  initial: WebShortcutConfig
  onClose: () => void
  onSave: (next: WebShortcutConfig) => void
  onRemove: () => void
}) {
  const [draftUrl, setDraftUrl] = useState(initial.url ?? '')
  const [draftLabel, setDraftLabel] = useState(initial.label ?? '')
  const [iconKind, setIconKind] = useState<'favicon' | 'preset' | 'custom'>(
    initial.iconKind ?? 'favicon',
  )
  const [presetName, setPresetName] = useState(initial.presetName ?? 'globe')
  const [customDataUrl, setCustomDataUrl] = useState(initial.customDataUrl ?? '')
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [previewBroken, setPreviewBroken] = useState(false)
  const [titleFetching, setTitleFetching] = useState(false)
  const [titleError, setTitleError] = useState<string | null>(null)
  // Manual favicon pick (clear = auto-resolve on server). Stored alongside
  // its bgColor so we don't re-probe on every dashboard render.
  const [faviconUrl, setFaviconUrl] = useState<string | undefined>(initial.faviconUrl)
  const [faviconBgColor, setFaviconBgColor] = useState<string | null | undefined>(
    initial.faviconBgColor,
  )

  async function handleFetchTitle() {
    const url = normalizeUrl(draftUrl)
    if (!url) {
      setTitleError('Erst eine URL eintragen.')
      return
    }
    setTitleError(null)
    setTitleFetching(true)
    try {
      const { title } = await api.fetchPageTitle(url)
      if (title?.trim()) {
        setDraftLabel(title.trim())
      } else {
        setTitleError('Kein Titel auf der Seite gefunden.')
      }
    } catch {
      setTitleError('Konnte Seite nicht laden.')
    } finally {
      setTitleFetching(false)
    }
  }

  function handleFile(file: File) {
    setUploadError(null)
    if (!file.type.startsWith('image/')) {
      setUploadError('Nur Bilddateien sind erlaubt.')
      return
    }
    if (file.size > MAX_CUSTOM_ICON_BYTES) {
      setUploadError(`Datei zu groß (max ${Math.round(MAX_CUSTOM_ICON_BYTES / 1024)} KB).`)
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result === 'string') {
        setCustomDataUrl(result)
        setIconKind('custom')
      }
    }
    reader.onerror = () => setUploadError('Datei konnte nicht gelesen werden.')
    reader.readAsDataURL(file)
  }

  function handleSave() {
    const url = normalizeUrl(draftUrl)
    if (!url) return
    // Label is optional and never auto-derived. Empty string = no label
    // shown on the tile, icon will centre itself.
    const label = draftLabel.trim()
    const next: WebShortcutConfig = { url, label, iconKind }
    if (iconKind === 'preset') next.presetName = presetName
    if (iconKind === 'custom') next.customDataUrl = customDataUrl
    if (iconKind === 'favicon' && faviconUrl) {
      next.faviconUrl = faviconUrl
      next.faviconBgColor = faviconBgColor ?? null
    }
    onSave(next)
  }

  // Live-preview cfg — uses draft values directly; an empty label means
  // the preview also shows the no-label centred layout.
  const previewCfg: WebShortcutConfig = {
    url: normalizeUrl(draftUrl) || initial.url,
    label: draftLabel,
    iconKind,
    presetName,
    customDataUrl,
    faviconUrl,
    faviconBgColor,
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      title="Web-Verknüpfung"
      description="URL, Label und Icon der Kachel. Änderungen werden erst beim Speichern übernommen."
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          {initial.url ? (
            <Button variant="ghost" onClick={onRemove} className="text-danger hover:text-danger">
              <Trash2 className="h-4 w-4" /> Entfernen
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              <X className="h-4 w-4" /> Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={!draftUrl.trim()}>
              Speichern
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Vorschau-Tile oben */}
        <div className="flex justify-center">
          <div className="relative h-20 w-20 rounded-2xl border border-border-subtle bg-bg-overlay/30 overflow-hidden">
            <ShortcutTileVisual
              cfg={previewCfg}
              iconBroken={previewBroken}
              onIconError={() => setPreviewBroken(true)}
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="ws-url">URL</Label>
          <Input
            id="ws-url"
            type="url"
            value={draftUrl}
            onChange={(e) => setDraftUrl(e.target.value)}
            placeholder="https://example.com"
            autoFocus
          />
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="ws-label">Label (optional)</Label>
            <button
              type="button"
              onClick={handleFetchTitle}
              disabled={titleFetching || !draftUrl.trim()}
              className="text-[11px] text-accent hover:underline disabled:text-text-tertiary disabled:no-underline disabled:cursor-not-allowed"
            >
              {titleFetching ? 'Lade …' : 'Titel von URL holen'}
            </button>
          </div>
          <Input
            id="ws-label"
            type="text"
            value={draftLabel}
            onChange={(e) => setDraftLabel(e.target.value)}
            placeholder="Leer lassen für reines Icon"
          />
          {titleError && <p className="text-[11px] text-danger">{titleError}</p>}
        </div>

        <div className="space-y-2">
          <Label>Icon</Label>
          <div className="flex gap-1 rounded-md bg-bg-overlay/40 p-0.5 text-xs">
            {(
              [
                { id: 'favicon', label: 'Favicon' },
                { id: 'preset', label: 'Vorlage' },
                { id: 'custom', label: 'Eigenes' },
              ] as const
            ).map((opt) => {
              const active = iconKind === opt.id
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setIconKind(opt.id)}
                  className={`flex-1 rounded px-2.5 py-1 transition-colors ${
                    active
                      ? 'bg-accent/15 text-accent font-medium'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>

          {iconKind === 'favicon' && (
            <FaviconPicker
              pageUrl={normalizeUrl(draftUrl)}
              selected={faviconUrl}
              onSelect={(iconUrl, bgColor) => {
                setFaviconUrl(iconUrl)
                setFaviconBgColor(bgColor)
              }}
              onClear={() => {
                setFaviconUrl(undefined)
                setFaviconBgColor(undefined)
              }}
              fallbackColor={deriveTileColor(normalizeUrl(draftUrl))}
            />
          )}

          {iconKind === 'preset' && (
            <div className="grid grid-cols-6 gap-1.5 max-h-48 overflow-y-auto rounded-md border border-border-subtle bg-bg-overlay/30 p-2">
              {ICON_PRESETS.map((p) => {
                const Icon = p.icon
                const active = p.name === presetName
                return (
                  <button
                    key={p.name}
                    type="button"
                    onClick={() => setPresetName(p.name)}
                    title={p.label}
                    className={`flex items-center justify-center h-9 rounded-md border transition-colors ${
                      active
                        ? 'border-accent ring-1 ring-accent/40'
                        : 'border-border-subtle hover:border-border-default'
                    }`}
                    style={{ backgroundColor: `${p.tint}1a` }}
                  >
                    <Icon className="h-4 w-4" style={{ color: p.tint }} />
                  </button>
                )
              })}
            </div>
          )}

          {iconKind === 'custom' && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer text-xs text-text-secondary border border-dashed border-border-default rounded-md px-3 py-2 hover:border-accent/60 hover:text-text-primary transition-colors">
                <Upload className="h-4 w-4" />
                <span>{customDataUrl ? 'Bild austauschen …' : 'Bild auswählen …'}</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFile(file)
                  }}
                />
              </label>
              {uploadError && <p className="text-xs text-danger">{uploadError}</p>}
              <p className="text-2xs text-text-tertiary">
                PNG/SVG/JPG, max {Math.round(MAX_CUSTOM_ICON_BYTES / 1024)} KB.
              </p>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  )
}

// === Absence Overview ========================================================

const ABS_TYPE_LABEL: Record<AbsenceType, string> = {
  vacation: 'Urlaub',
  sick: 'Krank',
  training: 'Fortbildung',
  other: 'Sonstiges',
  overtime_reduction: 'Überstundenabbau',
  maternity: 'Mutterschutz',
  parental: 'Elternzeit',
}

const ABS_TYPE_ICON: Record<AbsenceType, typeof Briefcase> = {
  vacation: Briefcase,
  sick: Stethoscope,
  training: GraduationCap,
  other: MoreHorizontal,
  overtime_reduction: MoreHorizontal,
  maternity: MoreHorizontal,
  parental: MoreHorizontal,
}

const ABS_TYPE_COLOR: Record<AbsenceType, string> = {
  vacation: '#3B82F6',
  sick: '#EF4444',
  training: '#10B981',
  other: '#A3978C',
  overtime_reduction: '#A3978C',
  maternity: '#A3978C',
  parental: '#A3978C',
}

function AbsenceOverviewWidget(_props: WidgetRenderProps) {
  const [absenceDialog, setAbsenceDialog] = useState<{
    open: boolean
    absence: Absence | null
  }>({ open: false, absence: null })

  const now = new Date()
  const yearStart = `${now.getFullYear()}-01-01`
  const yearEnd = `${now.getFullYear()}-12-31`

  const { data, isLoading } = useQuery({
    queryKey: ['absences', 'widget', yearStart, yearEnd],
    queryFn: () => api.listAbsences({ from: yearStart, to: yearEnd }),
  })
  const { data: meData } = useQuery({ queryKey: ['auth', 'me'], queryFn: () => api.me() })
  const holidays = useHolidaysInRange(meData?.user ?? null, yearStart, yearEnd)
  const schedules = useWorkSchedulesForUser(meData?.user ?? null)

  // Abgelehnte/zurückgezogene/stornierte Anträge zählen weder in die
  // Summenkarten noch in die „Geplant"-Liste — vorher erschien dort sogar
  // abgelehnter Urlaub (AbsenceService.list liefert alle Status).
  const absences = (data?.absences ?? []).filter(
    (a) => a.status === 'approved' || a.status === 'pending',
  )
  const todayIso = now.toISOString().slice(0, 10)

  // Kanonischer Zähler (wie Stundenkonto): feiertags-/teilzeit-aware,
  // Halbtage 0,5, jahresübergreifend geclippt — der frühere lokale Mo–Fr-
  // Zähler wich sichtbar vom Urlaubskonto ab (Audit-Finding).
  const baseOpts = {
    holidays,
    targetMinutesForDay:
      schedules.length > 0
        ? (iso: string) => targetMinutesForDay(new Date(`${iso}T12:00:00`), schedules)
        : undefined,
  }
  const countOpts = { ...baseOpts, year: now.getFullYear() }
  const canonicalDays = (list: Absence[], opts: CountAbsenceDaysOptions = countOpts) =>
    countAbsenceDays(
      list.map((a) => ({
        fromDate: a.fromDate.slice(0, 10),
        toDate: a.toDate.slice(0, 10),
        type: a.type,
        status: a.status,
        firstDayHalf: a.firstDayHalf,
        lastDayHalf: a.lastDayHalf,
      })),
      opts,
    )
  const daysByType = (type: AbsenceType) => canonicalDays(absences.filter((a) => a.type === type))

  const upcoming = absences
    .filter((a) => new Date(a.toDate) >= new Date(todayIso))
    .sort((a, b) => new Date(a.fromDate).getTime() - new Date(b.fromDate).getTime())
    .slice(0, 4)

  const types: AbsenceType[] = ['vacation', 'sick', 'training', 'other']

  return (
    <>
      <div className="h-full flex flex-col gap-3 min-h-0">
        {/* Summary cards */}
        <div className="grid grid-cols-2 gap-2 shrink-0">
          {types.map((t) => {
            const Icon = ABS_TYPE_ICON[t]
            const color = ABS_TYPE_COLOR[t]
            const days = daysByType(t)
            return (
              <div
                key={t}
                className="flex items-center gap-2 rounded-lg border border-border-subtle bg-bg-overlay/30 px-3 py-2"
              >
                <div
                  className="flex h-7 w-7 items-center justify-center rounded-md shrink-0"
                  style={{ backgroundColor: `${color}20`, color }}
                >
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xs text-text-tertiary truncate">{ABS_TYPE_LABEL[t]}</p>
                  <p className="font-mono tabular-nums text-sm font-light text-text-primary">
                    {days} {days === 1 ? 'Tag' : 'Tage'}
                  </p>
                </div>
              </div>
            )
          })}
        </div>

        {/* Upcoming absences */}
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex items-center justify-between mb-1.5 shrink-0">
            <p className="text-2xs uppercase tracking-wider text-text-tertiary font-medium">
              Geplant
            </p>
            <button
              type="button"
              onClick={() => setAbsenceDialog({ open: true, absence: null })}
              className="flex items-center gap-0.5 text-2xs text-accent hover:underline"
            >
              <Plus className="h-3 w-3" /> Neu
            </button>
          </div>
          {isLoading ? (
            <p className="text-xs text-text-tertiary">Lade …</p>
          ) : upcoming.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-xs text-text-tertiary">Keine geplanten Abwesenheiten</p>
            </div>
          ) : (
            <ul className="space-y-1 overflow-y-auto flex-1 min-h-0">
              {upcoming.map((a) => {
                const Icon = ABS_TYPE_ICON[a.type]
                const color = ABS_TYPE_COLOR[a.type]
                // Pro-Eintrag-Anzeige: volle Dauer, OHNE Jahres-Clipping —
                // das Clipping gehört nur in die Jahres-Summenkarten.
                const days = canonicalDays([a], baseOpts)
                const fromFmt = new Date(a.fromDate).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: 'short',
                })
                const toFmt = new Date(a.toDate).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: 'short',
                })
                return (
                  <li
                    key={a.id}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-bg-overlay/40 transition-colors cursor-pointer"
                    onClick={() => setAbsenceDialog({ open: true, absence: a })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setAbsenceDialog({ open: true, absence: a })
                    }}
                  >
                    <div
                      className="flex h-6 w-6 items-center justify-center rounded shrink-0"
                      style={{ backgroundColor: `${color}20`, color }}
                    >
                      <Icon className="h-3 w-3" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-text-primary truncate">
                        {ABS_TYPE_LABEL[a.type]}
                      </p>
                      <p className="text-2xs text-text-tertiary">
                        {fromFmt} – {toFmt} · {days}d
                      </p>
                    </div>
                    <ChevronRight className="h-3 w-3 text-text-tertiary shrink-0" />
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      <AbsenceFormDialog
        open={absenceDialog.open}
        absence={absenceDialog.absence}
        onClose={() => setAbsenceDialog({ open: false, absence: null })}
      />
    </>
  )
}

// === Vacation Balance ========================================================
// Tankanzeige + 4 KPI cards. Reads TimeAccountData (already exposes
// vacationDaysEntitled/Taken/Planned/Remaining + carryover with expiry).

function fmtVacationDays(n: number | null | undefined): string {
  if (n == null) return '–'
  // Trim trailing .0 but keep .5 for half-day precision.
  const rounded = Math.round(n * 10) / 10
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace('.', ',')
}

function VacationBalanceWidget(_props: WidgetRenderProps) {
  const year = new Date().getFullYear()
  const { data, isLoading } = useQuery({
    queryKey: ['time-accounts', 'self', year],
    queryFn: () => api.getTimeAccounts({ year }),
  })

  if (isLoading) return <p className="text-sm text-text-tertiary p-4">Lade …</p>
  if (!data) return <p className="text-sm text-text-tertiary p-4">Keine Daten</p>

  const entitled = data.vacationDaysEntitled ?? 0
  const carryover = data.vacationCarryoverDays ?? 0
  const taken = data.vacationDaysTaken ?? 0
  const planned = data.vacationDaysPlanned ?? 0
  const remainingRaw =
    data.vacationDaysRemaining != null
      ? data.vacationDaysRemaining
      : entitled + carryover - taken - planned
  const remaining = Math.max(0, remainingRaw)

  // Total pool for the progress bar — entitlement + carryover.
  const total = entitled + carryover
  const takenPct = total > 0 ? Math.min(100, (taken / total) * 100) : 0
  const plannedPct = total > 0 ? Math.min(100 - takenPct, (planned / total) * 100) : 0

  // Carryover expiry date — usually 31.03 of following year. Picked from
  // the first carryover-typed adjustment that still has an expiresAt.
  const carryoverAdj = (data.vacationAdjustments ?? []).find(
    (a) => a.type === 'carryover' && a.expiresAt,
  )
  const carryoverExpiresAt = carryoverAdj?.expiresAt
    ? new Date(carryoverAdj.expiresAt).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : null
  // Days remaining until the carryover expires. ceil so "today is the last day"
  // still reads "1 Tag" rather than "0 Tage". Banner only shows when ≤ 30 days.
  const carryoverDaysToExpiry = carryoverAdj?.expiresAt
    ? Math.ceil((new Date(carryoverAdj.expiresAt).getTime() - Date.now()) / 86_400_000)
    : null
  const showCarryoverWarning =
    carryover > 0 &&
    carryoverDaysToExpiry != null &&
    carryoverDaysToExpiry >= 0 &&
    carryoverDaysToExpiry <= 30

  return (
    <div className="h-full flex flex-col gap-3 min-h-0">
      {/* Tankanzeige — segmented horizontal bar.
            - genommen: solid accent
            - geplant: striped accent/50 ("noch nicht final")
            - verbleibend: text-tertiary/20 (light fill of the track) */}
      <div className="shrink-0">
        <div className="flex items-baseline justify-between mb-1.5">
          <p className="text-2xs uppercase tracking-wider text-text-tertiary font-medium">
            Urlaubskonto {year}
          </p>
          <p className="text-2xs text-text-tertiary">
            {fmtVacationDays(taken + planned)} / {fmtVacationDays(total)} Tage
          </p>
        </div>
        <div
          className="relative h-3 w-full rounded-full overflow-hidden border border-border-subtle"
          style={{ backgroundColor: 'rgb(var(--color-text-tertiary) / 0.18)' }}
          title={`Genommen ${fmtVacationDays(taken)} · Geplant ${fmtVacationDays(planned)} · Frei ${fmtVacationDays(remaining)}`}
        >
          {/* Genommen — solid accent */}
          <div className="absolute inset-y-0 left-0 bg-accent" style={{ width: `${takenPct}%` }} />
          {/* Geplant — striped accent/50 */}
          <div
            className="absolute inset-y-0 bg-accent/50"
            style={{
              left: `${takenPct}%`,
              width: `${plannedPct}%`,
              backgroundImage:
                'repeating-linear-gradient(45deg, rgba(255,255,255,0.18) 0px, rgba(255,255,255,0.18) 4px, transparent 4px, transparent 8px)',
            }}
          />
        </div>
        <div className="flex items-center justify-between text-2xs text-text-tertiary mt-1 gap-2">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm bg-accent" /> Genommen
          </span>
          <span className="inline-flex items-center gap-1">
            <span
              className="inline-block w-2 h-2 rounded-sm bg-accent/50"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(45deg, rgba(255,255,255,0.18) 0px, rgba(255,255,255,0.18) 2px, transparent 2px, transparent 4px)',
              }}
            />{' '}
            Geplant
          </span>
          <span className="inline-flex items-center gap-1">
            <span
              className="inline-block w-2 h-2 rounded-sm border border-border-default"
              style={{ backgroundColor: 'rgb(var(--color-text-tertiary) / 0.18)' }}
            />{' '}
            Verbleibend
          </span>
        </div>
      </div>

      {/* Carryover expiry warning — only when within 30 days. */}
      {showCarryoverWarning && carryoverExpiresAt && carryoverDaysToExpiry != null && (
        <div className="shrink-0 rounded-md border border-warning/40 bg-warning/10 px-2.5 py-1.5 flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-warning shrink-0" />
          <p className="text-2xs text-warning leading-tight">
            Übertrag verfällt am {carryoverExpiresAt} — noch {carryoverDaysToExpiry}{' '}
            {carryoverDaysToExpiry === 1 ? 'Tag' : 'Tage'}.
          </p>
        </div>
      )}

      {/* KPI grid — 2x2. Each card has a tooltip explaining how it's calculated. */}
      <div className="grid grid-cols-2 gap-2 flex-1 min-h-0 content-start">
        <KpiCard
          label="Anspruch"
          value={fmtVacationDays(entitled)}
          suffix="Tage"
          tone="text-text-primary"
          tooltip={`Jahresanspruch ${year}${
            data.vacationAdjustmentDays
              ? ` inkl. ${data.vacationAdjustmentDays > 0 ? '+' : ''}${data.vacationAdjustmentDays} Tage Anpassung`
              : ''
          }`}
        />
        <KpiCard
          label={carryoverExpiresAt ? `Übertrag bis ${carryoverExpiresAt}` : 'Übertrag'}
          value={fmtVacationDays(carryover)}
          suffix="Tage"
          tone={carryover > 0 ? 'text-amber-400' : 'text-text-tertiary'}
          tooltip={
            carryoverExpiresAt
              ? `Restanspruch aus dem Vorjahr, verfällt am ${carryoverExpiresAt}.`
              : 'Restanspruch aus dem Vorjahr.'
          }
        />
        <KpiCard
          label="Genommen"
          value={fmtVacationDays(taken)}
          suffix="Tage"
          tone="text-accent"
          tooltip="Genehmigte Abwesenheiten, deren Zeitraum begonnen hat oder vorbei ist."
        />
        <KpiCard
          label="Geplant"
          value={fmtVacationDays(planned)}
          suffix="Tage"
          tone="text-accent/70"
          tooltip="Genehmigte oder beantragte Abwesenheiten in der Zukunft."
        />
      </div>

      {/* Verbleibend — emphasised footer. Tooltip shows the calculation. */}
      <div
        className="shrink-0 rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 flex items-baseline justify-between"
        title={`Anspruch ${fmtVacationDays(entitled)} + Übertrag ${fmtVacationDays(carryover)} − Genommen ${fmtVacationDays(taken)} − Geplant ${fmtVacationDays(planned)} = Verbleibend ${fmtVacationDays(remaining)}`}
      >
        <span className="text-2xs uppercase tracking-wider text-text-tertiary font-medium">
          Verbleibend
        </span>
        <span className="font-mono tabular-nums text-lg font-light text-accent">
          {fmtVacationDays(remaining)} <span className="text-2xs text-text-tertiary">Tage</span>
        </span>
      </div>
    </div>
  )
}

function KpiCard({
  label,
  value,
  suffix,
  tone,
  tooltip,
}: {
  label: string
  value: string
  suffix?: string
  tone?: string
  tooltip?: string
}) {
  return (
    <div
      className="rounded-lg border border-border-subtle bg-bg-overlay/30 px-2.5 py-2 min-w-0"
      title={tooltip}
    >
      <p className="text-2xs uppercase tracking-wider text-text-tertiary font-medium truncate">
        {label}
      </p>
      <p className={cn('font-mono tabular-nums text-base font-light', tone ?? 'text-text-primary')}>
        {value}
        {suffix && <span className="text-2xs text-text-tertiary ml-1">{suffix}</span>}
      </p>
    </div>
  )
}

// ── Tax Deadlines Widget ────────────────────────────────────────────

interface TaxDeadline {
  day: number
  label: string
  description: string
  category: 'ust' | 'lst' | 'est' | 'kst' | 'gwst' | 'sv' | 'other'
  monthly?: boolean
  quarterly?: boolean
  yearly?: boolean
  months?: number[]
}

const TAX_CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  ust: { bg: 'bg-blue-500/15', text: 'text-blue-400' },
  lst: { bg: 'bg-amber-500/15', text: 'text-amber-400' },
  est: { bg: 'bg-emerald-500/15', text: 'text-emerald-400' },
  kst: { bg: 'bg-purple-500/15', text: 'text-purple-400' },
  gwst: { bg: 'bg-rose-500/15', text: 'text-rose-400' },
  sv: { bg: 'bg-cyan-500/15', text: 'text-cyan-400' },
  other: { bg: 'bg-text-tertiary/15', text: 'text-text-secondary' },
}

const TAX_DEADLINES: TaxDeadline[] = [
  {
    day: 10,
    label: 'Lohnsteuer',
    description: 'Lohnsteuer-Anmeldung + Zahlung',
    category: 'lst',
    monthly: true,
  },
  {
    day: 10,
    label: 'Umsatzsteuer',
    description: 'USt-Voranmeldung + Zahlung',
    category: 'ust',
    monthly: true,
  },
  {
    day: 10,
    label: 'Einkommensteuer',
    description: 'ESt-Vorauszahlung',
    category: 'est',
    quarterly: true,
    months: [3, 6, 9, 12],
  },
  {
    day: 10,
    label: 'Körperschaftsteuer',
    description: 'KSt-Vorauszahlung',
    category: 'kst',
    quarterly: true,
    months: [3, 6, 9, 12],
  },
  {
    day: 15,
    label: 'Gewerbesteuer',
    description: 'GewSt-Vorauszahlung',
    category: 'gwst',
    quarterly: true,
    months: [2, 5, 8, 11],
  },
  {
    day: 15,
    label: 'Zusammenfassende Meldung',
    description: 'ZM an das BZSt (EU-Lieferungen)',
    category: 'ust',
    monthly: true,
  },
  {
    day: 28,
    label: 'Sozialversicherung',
    description: 'SV-Beiträge (drittletzter Bankarbeitstag)',
    category: 'sv',
    monthly: true,
  },
  {
    day: 31,
    label: 'Jahresmeldung SV',
    description: 'Jahresmeldung Sozialversicherung',
    category: 'sv',
    yearly: true,
    months: [2],
  },
  {
    day: 31,
    label: 'USt-Jahreserklärung',
    description: 'Umsatzsteuer-Jahreserklärung (Frist mit Berater: 28.02. Folgejahr)',
    category: 'ust',
    yearly: true,
    months: [7],
  },
  {
    day: 31,
    label: 'ESt-Erklärung',
    description: 'Einkommensteuer-Erklärung (Frist mit Berater: 31.07. Folgejahr)',
    category: 'est',
    yearly: true,
    months: [7],
  },
  {
    day: 31,
    label: 'GewSt-Erklärung',
    description: 'Gewerbesteuer-Erklärung (Frist mit Berater: 31.07. Folgejahr)',
    category: 'gwst',
    yearly: true,
    months: [7],
  },
  {
    day: 31,
    label: 'KSt-Erklärung',
    description: 'Körperschaftsteuer-Erklärung (Frist mit Berater: 31.07. Folgejahr)',
    category: 'kst',
    yearly: true,
    months: [7],
  },
  {
    day: 1,
    label: 'Inventur',
    description: 'Stichtags-Inventur zum Geschäftsjahresende',
    category: 'other',
    yearly: true,
    months: [1],
  },
]

function getDeadlinesForMonth(
  month: number,
  source: TaxDeadline[] = TAX_DEADLINES,
): Array<TaxDeadline & { isPast: boolean; isToday: boolean; dateStr: string }> {
  const now = new Date()
  const currentDay = now.getDate()
  const currentMonth = now.getMonth() + 1
  const year = now.getFullYear()

  return source
    .filter((d) => {
      if (d.monthly) return true
      if (d.quarterly && d.months?.includes(month)) return true
      if (d.yearly && d.months?.includes(month)) return true
      return false
    })
    .map((d) => {
      const deadlineDay = Math.min(d.day, new Date(year, month, 0).getDate())
      const isPast = month === currentMonth ? currentDay > deadlineDay : month < currentMonth
      const isToday = month === currentMonth && currentDay === deadlineDay
      return {
        ...d,
        isPast,
        isToday,
        dateStr: `${String(deadlineDay).padStart(2, '0')}.${String(month).padStart(2, '0')}.`,
      }
    })
    .sort((a, b) => a.day - b.day)
}

interface TaxDeadlineConfig {
  disabledLabels?: string[]
  customDeadlines?: Array<{
    day: number
    label: string
    description: string
    category: string
    months: number[]
  }>
}

function TaxDeadlinesWidget({ config, onConfigChange, editMode }: WidgetRenderProps) {
  const cfg = config as TaxDeadlineConfig
  const disabledSet = new Set(cfg.disabledLabels ?? [])
  const [showConfig, setShowConfig] = useState(false)
  const [newDay, setNewDay] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [newDesc, setNewDesc] = useState('')

  const customAsDeadlines: TaxDeadline[] = (cfg.customDeadlines ?? []).map((d) => ({
    day: d.day,
    label: d.label,
    description: d.description,
    category: (d.category || 'other') as TaxDeadline['category'],
    monthly: d.months.length === 0 || d.months.length === 12,
    months: d.months,
  }))

  const now = new Date()
  const currentMonth = now.getMonth() + 1
  const monthName = now.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
  const allForMonth = getDeadlinesForMonth(currentMonth, [...TAX_DEADLINES, ...customAsDeadlines])
  const deadlines = allForMonth.filter((d) => !disabledSet.has(d.label))

  const upcoming = deadlines.filter((d) => !d.isPast)
  const past = deadlines.filter((d) => d.isPast)

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-1 mb-2">
        <span className="text-xs font-medium text-text-tertiary uppercase tracking-wider">
          {monthName}
        </span>
        <span className="text-2xs text-text-tertiary">{upcoming.length} ausstehend</span>
      </div>
      <div className="flex-1 overflow-y-auto space-y-1">
        {deadlines.length === 0 ? (
          <p className="text-xs text-text-tertiary text-center py-4">
            Keine Termine in diesem Monat
          </p>
        ) : (
          <>
            {upcoming.map((d) => (
              <div
                key={`u-${d.label}-${d.day}`}
                className={cn(
                  'flex items-start gap-2 rounded-lg px-2 py-1.5 transition-colors',
                  d.isToday ? 'bg-accent/10 border border-accent/30' : 'hover:bg-bg-overlay/40',
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 text-2xs font-mono tabular-nums w-12 shrink-0',
                    d.isToday ? 'text-accent font-bold' : 'text-text-tertiary',
                  )}
                >
                  {d.dateStr}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        'px-1.5 py-px text-2xs uppercase tracking-wide rounded font-medium',
                        TAX_CATEGORY_COLORS[d.category]?.bg,
                        TAX_CATEGORY_COLORS[d.category]?.text,
                      )}
                    >
                      {d.label}
                    </span>
                  </div>
                  <p className="text-2xs text-text-tertiary mt-0.5 truncate">{d.description}</p>
                </div>
                {d.isToday && (
                  <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-accent mt-1.5 animate-pulse" />
                )}
              </div>
            ))}
            {past.length > 0 && (
              <>
                <div className="border-t border-border-subtle my-1.5" />
                <p className="text-2xs text-text-tertiary uppercase tracking-wider px-2">
                  Erledigt
                </p>
                {past.map((d) => (
                  <div
                    key={`p-${d.label}-${d.day}`}
                    className="flex items-start gap-2 rounded-lg px-2 py-1 opacity-40"
                  >
                    <span className="mt-0.5 text-2xs font-mono tabular-nums w-12 shrink-0 text-text-tertiary line-through">
                      {d.dateStr}
                    </span>
                    <div className="min-w-0 flex-1">
                      <span
                        className={cn(
                          'px-1.5 py-px text-2xs uppercase tracking-wide rounded font-medium',
                          TAX_CATEGORY_COLORS[d.category]?.bg,
                          TAX_CATEGORY_COLORS[d.category]?.text,
                        )}
                      >
                        {d.label}
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
      {editMode && (
        <div className="border-t border-border-subtle mt-2 pt-2">
          <button
            type="button"
            onClick={() => setShowConfig(!showConfig)}
            className="text-2xs text-accent hover:text-accent/80 transition-colors"
          >
            {showConfig ? 'Konfiguration schließen' : '⚙ Termine konfigurieren'}
          </button>
          {showConfig && (
            <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
              <p className="text-2xs text-text-tertiary uppercase tracking-wider">
                Standard-Termine
              </p>
              {TAX_DEADLINES.map((d) => (
                <label
                  key={d.label}
                  className="flex items-center gap-2 text-2xs text-text-secondary cursor-pointer hover:text-text-primary"
                >
                  <input
                    type="checkbox"
                    checked={!disabledSet.has(d.label)}
                    onChange={() => {
                      const next = new Set(disabledSet)
                      if (next.has(d.label)) next.delete(d.label)
                      else next.add(d.label)
                      onConfigChange({ ...cfg, disabledLabels: Array.from(next) })
                    }}
                    className="accent-accent h-3 w-3"
                  />
                  <span className="truncate">{d.label}</span>
                </label>
              ))}
              <p className="text-2xs text-text-tertiary uppercase tracking-wider pt-2">
                Eigenen Termin hinzufügen
              </p>
              <div className="flex gap-1">
                <input
                  type="number"
                  min="1"
                  max="31"
                  placeholder="Tag"
                  value={newDay}
                  onChange={(e) => setNewDay(e.target.value)}
                  className="w-12 rounded border border-border-default bg-bg-overlay px-1.5 py-0.5 text-2xs text-text-primary"
                />
                <input
                  placeholder="Bezeichnung"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  className="flex-1 rounded border border-border-default bg-bg-overlay px-1.5 py-0.5 text-2xs text-text-primary"
                />
                <button
                  type="button"
                  disabled={!newDay || !newLabel}
                  onClick={() => {
                    const custom = [
                      ...(cfg.customDeadlines ?? []),
                      {
                        day: Number(newDay),
                        label: newLabel,
                        description: newDesc || newLabel,
                        category: 'other',
                        months: Array.from({ length: 12 }, (_, i) => i + 1),
                      },
                    ]
                    onConfigChange({ ...cfg, customDeadlines: custom })
                    setNewDay('')
                    setNewLabel('')
                    setNewDesc('')
                  }}
                  className="px-2 py-0.5 rounded bg-accent text-bg-base text-2xs font-medium disabled:opacity-30"
                >
                  +
                </button>
              </div>
              {(cfg.customDeadlines ?? []).length > 0 && (
                <div className="space-y-1">
                  {(cfg.customDeadlines ?? []).map((d, i) => (
                    <div
                      key={`c-${d.label}-${d.day}`}
                      className="flex items-center gap-1 text-2xs text-text-secondary"
                    >
                      <span className="font-mono w-6">{d.day}.</span>
                      <span className="truncate flex-1">{d.label}</span>
                      <button
                        type="button"
                        onClick={() => {
                          const custom = (cfg.customDeadlines ?? []).filter((_, j) => j !== i)
                          onConfigChange({ ...cfg, customDeadlines: custom })
                        }}
                        className="text-danger hover:text-danger/80 text-2xs"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// === Performance Overview ======================================================

type PerformanceScope = 'self' | 'team' | 'all'
type PerformanceTimeRange = 'today' | 'week' | 'month' | 'quarter' | 'year'

interface PerformanceConfig {
  scope?: PerformanceScope
  timeRange?: PerformanceTimeRange
}

function getPerformanceDateRange(range: PerformanceTimeRange): { from: string; to: string } {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const isoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  const to = isoDate(now)

  switch (range) {
    case 'today':
      return { from: to, to }
    case 'week': {
      const d = new Date(now)
      const day = d.getDay()
      const diff = day === 0 ? -6 : 1 - day
      d.setDate(d.getDate() + diff)
      return { from: isoDate(d), to }
    }
    case 'month': {
      const d = new Date(now.getFullYear(), now.getMonth(), 1)
      return { from: isoDate(d), to }
    }
    case 'quarter': {
      const qm = Math.floor(now.getMonth() / 3) * 3
      const d = new Date(now.getFullYear(), qm, 1)
      return { from: isoDate(d), to }
    }
    case 'year': {
      return { from: `${now.getFullYear()}-01-01`, to }
    }
  }
}

function PerformanceOverviewWidget({ config, onConfigChange }: WidgetRenderProps) {
  const cfg = config as PerformanceConfig
  const scope: PerformanceScope = cfg.scope ?? 'self'
  const timeRange: PerformanceTimeRange = cfg.timeRange ?? 'month'
  const { from, to } = getPerformanceDateRange(timeRange)

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'performance', scope, from, to],
    queryFn: () => api.performanceReport({ scope, from, to }),
  })
  const colors = useThemeColors()

  const setScope = (s: PerformanceScope) => onConfigChange({ ...cfg, scope: s })
  const setTimeRange = (r: PerformanceTimeRange) => onConfigChange({ ...cfg, timeRange: r })

  const billablePct =
    data && data.totalMinutes > 0 ? Math.round((data.billableMinutes / data.totalMinutes) * 100) : 0
  const revenueFormatted = data
    ? (data.revenueNetCents / 100).toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '0,00'

  const fmtHHMM = (minutes: number) => {
    const h = Math.floor(minutes / 60)
    const m = Math.round(minutes % 60)
    return `${h}:${String(m).padStart(2, '0')}`
  }

  return (
    <div className="h-full flex flex-col gap-2 min-h-0">
      {/* Scope + Time Range selectors */}
      <div className="flex items-center gap-2 shrink-0">
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value as PerformanceScope)}
          className="text-[11px] bg-bg-overlay border border-border-subtle rounded px-1.5 py-0.5 text-text-primary"
        >
          <option value="self">Ich</option>
          <option value="team">Team</option>
          <option value="all">Alle</option>
        </select>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value as PerformanceTimeRange)}
          className="text-[11px] bg-bg-overlay border border-border-subtle rounded px-1.5 py-0.5 text-text-primary"
        >
          <option value="today">Heute</option>
          <option value="week">Diese Woche</option>
          <option value="month">Dieser Monat</option>
          <option value="quarter">Dieses Quartal</option>
          <option value="year">Dieses Jahr</option>
        </select>
      </div>

      {isLoading ? (
        <p className="text-sm text-text-tertiary p-4">Lade …</p>
      ) : !data ? (
        <p className="text-sm text-text-tertiary p-4">Keine Daten</p>
      ) : (
        <>
          {/* 3 metric cards */}
          <div className="grid grid-cols-3 gap-2 shrink-0">
            <div className="rounded-lg border border-border-subtle bg-bg-overlay/30 px-2.5 py-2">
              <p className="text-2xs text-text-tertiary uppercase tracking-wider">Gesamtzeit</p>
              <p className="font-mono tabular-nums text-base font-light text-accent">
                {fmtHHMM(data.totalMinutes)}
              </p>
            </div>
            <div className="rounded-lg border border-border-subtle bg-bg-overlay/30 px-2.5 py-2">
              <p className="text-2xs text-text-tertiary uppercase tracking-wider">Umsatz Netto</p>
              <p className="font-mono tabular-nums text-base font-light text-text-primary">
                {revenueFormatted} €
              </p>
            </div>
            <div className="rounded-lg border border-border-subtle bg-bg-overlay/30 px-2.5 py-2">
              <p className="text-2xs text-text-tertiary uppercase tracking-wider">Abrechenbar</p>
              <p className="font-mono tabular-nums text-base font-light text-text-primary">
                {billablePct}%
              </p>
            </div>
          </div>

          {/* Charts: Pie + Bar side by side */}
          {data.activities.length > 0 ? (
            <div className="flex-1 min-h-0 grid grid-cols-2 gap-2">
              {/* Pie Chart */}
              <div className="min-h-0">
                <p className="text-2xs text-text-tertiary uppercase tracking-wider mb-1 px-1">
                  Tätigkeiten
                </p>
                <ResponsiveContainer width="100%" height="90%">
                  <PieChart>
                    <Pie
                      data={data.activities}
                      dataKey="minutes"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius="35%"
                      outerRadius="75%"
                      paddingAngle={2}
                      stroke="none"
                    >
                      {data.activities.map((a) => (
                        <Cell key={a.name} fill={a.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: colors.tooltipBg,
                        border: `1px solid ${colors.tooltipBorder}`,
                        borderRadius: 8,
                        fontSize: 11,
                      }}
                      labelStyle={{ color: colors.textPrimary }}
                      formatter={(value) => [`${fmtHHMM(Number(value))}`, 'Zeit']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Bar Chart */}
              <div className="min-h-0">
                <p className="text-2xs text-text-tertiary uppercase tracking-wider mb-1 px-1">
                  Stunden
                </p>
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart
                    data={data.activities.slice(0, 8)}
                    layout="vertical"
                    margin={{ top: 0, right: 4, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={colors.gridLine}
                      horizontal={false}
                    />
                    <XAxis
                      type="number"
                      tick={{ fill: colors.textTertiary, fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `${Math.round(v / 60)}h`}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={60}
                      tick={{ fill: colors.textSecondary, fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v: string) => (v.length > 8 ? `${v.slice(0, 7)}…` : v)}
                    />
                    <Tooltip
                      cursor={{ fill: colors.accentMuted }}
                      contentStyle={{
                        backgroundColor: colors.tooltipBg,
                        border: `1px solid ${colors.tooltipBorder}`,
                        borderRadius: 8,
                        fontSize: 11,
                      }}
                      labelStyle={{ color: colors.textPrimary }}
                      formatter={(value) => [`${fmtHHMM(Number(value))}`, 'Zeit']}
                    />
                    <Bar dataKey="minutes" radius={[0, 4, 4, 0]}>
                      {data.activities.slice(0, 8).map((a) => (
                        <Cell key={a.name} fill={a.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-xs text-text-tertiary">Keine Einträge im gewählten Zeitraum</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// === Team Status (Presence) ===================================================

const TEAM_ABS_LABEL: Record<string, string> = {
  vacation: 'Urlaub',
  sick: 'Krank',
  training: 'Fortbildung',
  other: 'Sonstiges',
}

const TEAM_ABS_COLOR: Record<string, string> = {
  vacation: '#3B82F6',
  sick: '#EF4444',
  training: '#10B981',
  other: '#A3978C',
}

function userInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

function fmtElapsed(ms: number): string {
  const totalMin = Math.floor(ms / 60_000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`
}

function TeamStatusWidget(_props: WidgetRenderProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'team-status'],
    queryFn: () => api.teamStatus(),
    refetchInterval: 60_000,
  })

  if (isLoading) return <p className="text-sm text-text-tertiary p-4">Lade …</p>
  if (!data) return <p className="text-sm text-text-tertiary p-4">Keine Daten</p>

  return (
    <div className="h-full flex flex-col gap-2 min-h-0 overflow-y-auto">
      {/* Absent section */}
      {data.absent.length > 0 && (
        <div>
          <p className="text-2xs uppercase tracking-wider text-text-tertiary font-medium mb-1 flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500" /> Abwesend (
            {data.absent.length})
          </p>
          <ul className="space-y-0.5">
            {data.absent.map((u) => (
              <li
                key={u.userId}
                className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-bg-overlay/40 transition-colors"
              >
                <div className="h-6 w-6 rounded-full bg-bg-overlay border border-border-subtle flex items-center justify-center text-2xs font-medium text-text-secondary shrink-0">
                  {userInitials(u.userName)}
                </div>
                <span className="text-xs text-text-primary truncate flex-1">{u.userName}</span>
                <span
                  className="text-2xs uppercase tracking-wide rounded px-1.5 py-px font-medium"
                  style={{
                    backgroundColor: `${TEAM_ABS_COLOR[u.type] ?? '#A3978C'}20`,
                    color: TEAM_ABS_COLOR[u.type] ?? '#A3978C',
                  }}
                >
                  {TEAM_ABS_LABEL[u.type] ?? u.type}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Active section */}
      {data.active.length > 0 && (
        <div>
          <p className="text-2xs uppercase tracking-wider text-text-tertiary font-medium mb-1 flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-green-500" /> Aktiv (
            {data.active.length})
          </p>
          <ul className="space-y-0.5">
            {data.active.map((u) => (
              <li
                key={u.userId}
                className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-bg-overlay/40 transition-colors"
              >
                <div className="h-6 w-6 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center text-2xs font-medium text-green-400 shrink-0">
                  {userInitials(u.userName)}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-text-primary truncate block">{u.userName}</span>
                  {u.projectName && (
                    <span className="text-2xs text-text-tertiary truncate block">
                      {u.projectName}
                    </span>
                  )}
                </div>
                <span className="text-2xs font-mono tabular-nums text-green-400 shrink-0">
                  {fmtElapsed(u.durationMs)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Available section */}
      {data.available.length > 0 && (
        <div>
          <p className="text-2xs uppercase tracking-wider text-text-tertiary font-medium mb-1 flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-text-tertiary opacity-50" />{' '}
            Verfügbar ({data.available.length})
          </p>
          <ul className="space-y-0.5">
            {data.available.map((u) => (
              <li
                key={u.userId}
                className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-bg-overlay/40 transition-colors"
              >
                <div className="h-6 w-6 rounded-full bg-bg-overlay border border-border-subtle flex items-center justify-center text-2xs font-medium text-text-tertiary shrink-0">
                  {userInitials(u.userName)}
                </div>
                <span className="text-xs text-text-secondary truncate flex-1">{u.userName}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.absent.length === 0 && data.active.length === 0 && data.available.length === 0 && (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-xs text-text-tertiary">Keine Teammitglieder sichtbar</p>
        </div>
      )}
    </div>
  )
}

// === Range Report =============================================================
// Dashboard variant of /auswertung's Range-Report. Embeds the SAME page
// body as the standalone route — Datums-Filter (von/bis), Projekt- /
// Kunden-Filter, RangeReportView mit Charts + byProject-Tabelle — nur
// kompakt gerendert via `embedded=true`. Die Tab-Bar wird ausgeblendet,
// Initial-Tab ist fix auf 'range' gesetzt.

type RangeReportTimeframe = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom'

interface RangeReportWidgetConfig {
  timeframe?: RangeReportTimeframe
}

const RANGE_REPORT_TIMEFRAME_LABELS: Record<RangeReportTimeframe, string> = {
  today: 'Heute',
  week: 'Diese Woche',
  month: 'Dieser Monat',
  quarter: 'Dieses Quartal',
  year: 'Dieses Jahr',
  custom: 'Eigener Zeitraum',
}

function toIsoDateLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function getRangeReportRange(timeframe: RangeReportTimeframe): { from: string; to: string } | null {
  if (timeframe === 'custom') return null
  const now = new Date()
  switch (timeframe) {
    case 'today':
      return { from: toIsoDateLocal(startOfDay(now)), to: toIsoDateLocal(endOfDay(now)) }
    case 'week':
      return { from: toIsoDateLocal(startOfWeek(now)), to: toIsoDateLocal(endOfWeek(now)) }
    case 'month':
      return { from: toIsoDateLocal(startOfMonth(now)), to: toIsoDateLocal(endOfMonth(now)) }
    case 'quarter': {
      const qStartMonth = Math.floor(now.getMonth() / 3) * 3
      const from = new Date(now.getFullYear(), qStartMonth, 1)
      const to = new Date(now.getFullYear(), qStartMonth + 3, 0)
      return { from: toIsoDateLocal(from), to: toIsoDateLocal(to) }
    }
    case 'year':
      return { from: toIsoDateLocal(startOfYear(now)), to: toIsoDateLocal(endOfYear(now)) }
  }
}

/** Kleines Export-Dropdown für CSV + PDF. Klick auf den Trigger öffnet
 *  eine Mini-Liste, Auswahl löst den Download über api.csvExportUrl /
 *  api.pdfReportUrl aus. Schließt sich bei Outside-Click + Esc. */
function RangeExportDropdown({ from, to }: { from: string; to: string }) {
  const [open, setOpen] = useState(false)
  // Outside-click + Esc
  useEffect(() => {
    if (!open) return
    function onPointer(e: PointerEvent) {
      const el = e.target as HTMLElement | null
      if (!el?.closest?.('[data-export-dropdown]')) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', onPointer, true)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('pointerdown', onPointer, true)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="relative" data-export-dropdown>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-[11px] bg-bg-overlay border border-border-subtle rounded px-1.5 py-0.5 text-text-primary hover:bg-bg-overlay/80 transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Download className="h-3 w-3" />
        Export
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-10 min-w-[100px] rounded-md border border-border-subtle bg-bg-elevated shadow-lg overflow-hidden"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <a
            role="menuitem"
            href={api.csvExportUrl({ from, to })}
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-1.5 text-[12px] text-text-primary hover:bg-bg-overlay/60 transition-colors"
          >
            <Download className="h-3.5 w-3.5 text-text-tertiary" />
            CSV
          </a>
          <a
            role="menuitem"
            href={api.pdfReportUrl({ from, to })}
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-1.5 text-[12px] text-text-primary hover:bg-bg-overlay/60 transition-colors border-t border-border-subtle"
          >
            <Download className="h-3.5 w-3.5 text-text-tertiary" />
            PDF
          </a>
        </div>
      )}
    </div>
  )
}

function RangeReportWidget({ config, onConfigChange }: WidgetRenderProps) {
  const cfg = config as RangeReportWidgetConfig
  const timeframe: RangeReportTimeframe = cfg.timeframe ?? 'week'
  const range = getRangeReportRange(timeframe)
  const setTimeframe = (tf: RangeReportTimeframe) => onConfigChange({ ...cfg, timeframe: tf })

  // Export-URLs für die aktuell ausgewählte Range. Für 'custom' (range==null)
  // fallen wir auf die Defaults zurück, die AuswertungPageBody auch verwendet
  // (firstOfMonth → today) — der Custom-Mode in der Body-UI bleibt
  // unabhängig editierbar; die Dropdown-Exports bilden dann die Default-
  // Range ab, was für die meisten Klicks reicht.
  const exportRange =
    range ??
    (() => {
      const now = new Date()
      const first = new Date(now.getFullYear(), now.getMonth(), 1)
      return { from: toIsoDateLocal(first), to: toIsoDateLocal(now) }
    })()

  return (
    <div className="h-full flex flex-col gap-2 min-h-0">
      {/* Header row: Zeitraum-Select + Export-Dropdown side by side. */}
      <div className="flex items-center gap-2 shrink-0 px-1">
        <span className="text-2xs uppercase tracking-wider text-text-tertiary">Zeitraum</span>
        <select
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value as RangeReportTimeframe)}
          className="text-[11px] bg-bg-overlay border border-border-subtle rounded px-1.5 py-0.5 text-text-primary"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {(Object.keys(RANGE_REPORT_TIMEFRAME_LABELS) as RangeReportTimeframe[]).map((tf) => (
            <option key={tf} value={tf}>
              {RANGE_REPORT_TIMEFRAME_LABELS[tf]}
            </option>
          ))}
        </select>
        <div className="ml-auto">
          <RangeExportDropdown from={exportRange.from} to={exportRange.to} />
        </div>
      </div>

      {/* Full /auswertung body. key-change forces a remount so a new
         timeframe applies its initial range; in 'custom' mode the key is
         stable so the user's manual inputs survive. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <AuswertungPageBody
          key={range ? `${timeframe}-${range.from}-${range.to}` : 'custom'}
          embedded
          initialTab="range"
          lockedTab
          hideTabs
          initialFrom={range?.from}
          initialTo={range?.to}
        />
      </div>
    </div>
  )
}

export function MyTasksWidget(_props: WidgetRenderProps) {
  const { data: meData } = useQuery({ queryKey: ['auth', 'me'], queryFn: () => api.me() })
  const { data, isLoading } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => api.listTasks(),
    enabled: !!meData?.user,
  })
  const myId = meData?.user?.id
  const todayISO = localTodayISO()
  const open = (data?.tasks ?? [])
    .filter((t) => t.assigneeId === myId && t.status !== 'done')
    .sort((a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'))
    .slice(0, 8)

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {isLoading ? (
        <p className="text-sm text-text-tertiary">Lade…</p>
      ) : open.length === 0 ? (
        <p className="text-sm text-text-tertiary">Keine offenen Aufgaben. 🎉</p>
      ) : (
        <div className="min-h-0 flex-1 divide-y divide-border-subtle overflow-y-auto">
          {open.map((t) => (
            <Link
              key={t.id}
              to="/tasks"
              className="flex items-center gap-2 py-1.5 text-sm hover:text-accent"
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 shrink-0 rounded-full',
                  t.priority === 'critical' || t.priority === 'high'
                    ? 'bg-danger'
                    : 'bg-text-tertiary',
                )}
              />
              <span className="min-w-0 flex-1 truncate text-text-primary">{t.title}</span>
              {t.dueDate && (
                <span
                  className={cn(
                    'shrink-0 font-mono text-2xs tabular-nums',
                    t.dueDate < todayISO ? 'text-danger' : 'text-text-tertiary',
                  )}
                >
                  {t.dueDate.slice(5)}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
      <Link to="/tasks" className="shrink-0 text-2xs text-accent hover:underline">
        Alle Aufgaben →
      </Link>
    </div>
  )
}

function ProjectBudgetWidget({ config, onConfigChange }: WidgetRenderProps) {
  const budget = useBudgetProjects(
    localTodayISO(),
    typeof config.projectId === 'string' ? config.projectId : null,
    (projectId, date) => api.getBudgetStatus(projectId, { date }),
  )
  const { status } = budget
  return (
    <section
      className="h-full min-h-0 overflow-y-auto space-y-1"
      aria-label="Abrechenbares Kontingent"
    >
      <BudgetProjectSelect
        {...budget}
        onChange={(projectId) => onConfigChange({ ...config, projectId })}
      />
      {budget.isLoading && !status ? (
        <p className="text-sm text-text-tertiary">Lade Kontingent …</p>
      ) : hasBudgetData(status) ? (
        <>
          <p className="text-[11px] text-text-secondary">
            <BudgetPeriodLabel status={status} />
          </p>
          <div className="grid grid-cols-2 gap-3">
            <BudgetSummary status={status} compact />
            <div className="border-l border-border-subtle pl-3">
              <BudgetRates status={status} rows />
            </div>
          </div>
          <p className="text-[10px] text-text-tertiary">
            Deine Raten · Feiertage &amp; Urlaub berücksichtigt
          </p>
        </>
      ) : (
        <p className="text-sm text-text-tertiary">
          {budget.isError ? 'Kontingent nicht verfügbar.' : 'Kein Kontingent für heute'}
        </p>
      )}
    </section>
  )
}

const WIDGETS: Record<WidgetType, WidgetDef> = {
  'project-budget': {
    type: 'project-budget',
    label: 'Abrechenbar',
    description: 'Projektkontingent, Rest und deine Raten für heute, Woche und Monat.',
    icon: Gauge,
    defaultSize: { w: 4, h: 5 },
    minSize: { w: 3, h: 4 },
    minPixels: { width: 240, height: 180 },
    Render: ProjectBudgetWidget,
  },
  'weekly-soll-ist': {
    type: 'weekly-soll-ist',
    label: 'Soll vs. Ist · Woche',
    description: 'Bar-Chart der laufenden Woche, Soll-Linie, Σ unten.',
    icon: BarChart3,
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 4, h: 5 },
    minPixels: { width: 320, height: 240 },
    Render: WeeklySollIstWidget,
  },
  'web-shortcut': {
    type: 'web-shortcut',
    label: 'Web-Verknüpfung',
    description: 'App-Kachel mit Favicon oder Icon. Feste, kompakte Größe.',
    icon: Globe,
    // Locked to the smallest near-square size — at the default container
    // width (cellW ≈ 89px, rowH 40 + 12 gap = 92px) one cell of 1×2 renders
    // as a roughly square 89×92 tile. resizable: false keeps it that way so
    // the tile is always one clean shape, no resize handle clipping the
    // rounded corner.
    defaultSize: { w: 1, h: 2 },
    minSize: { w: 1, h: 2 },
    minPixels: { width: 80, height: 80 },
    chromeless: true,
    resizable: false,
    Render: WebShortcutWidget,
  },
  'absence-overview': {
    type: 'absence-overview',
    label: 'Abwesenheiten',
    description: 'Jahresübersicht Urlaub, Krank, Fortbildung + geplante Einträge.',
    icon: CalendarOff,
    defaultSize: { w: 4, h: 6 },
    minSize: { w: 3, h: 4 },
    minPixels: { width: 260, height: 200 },
    Render: AbsenceOverviewWidget,
  },
  'vacation-balance': {
    type: 'vacation-balance',
    label: 'Urlaubskonto',
    description: 'Anspruch, Übertrag, Genommen und Geplant — mit Tankanzeige.',
    icon: Gauge,
    defaultSize: { w: 4, h: 6 },
    minSize: { w: 3, h: 4 },
    minPixels: { width: 260, height: 220 },
    Render: VacationBalanceWidget,
  },
  'tax-deadlines': {
    type: 'tax-deadlines',
    label: 'Steuertermine',
    description: 'Wichtige Steuertermine des aktuellen Monats (USt, LSt, etc.).',
    icon: CalendarIcon,
    defaultSize: { w: 4, h: 8 },
    minSize: { w: 3, h: 5 },
    minPixels: { width: 260, height: 200 },
    Render: TaxDeadlinesWidget,
  },
  'performance-overview': {
    type: 'performance-overview',
    label: 'Leistungsübersicht',
    description: 'Gesamtzeit, Umsatz, Abrechenbarkeit + Tätigkeiten als Kreis- und Balkendiagramm.',
    icon: TrendingUp,
    defaultSize: { w: 6, h: 10 },
    minSize: { w: 4, h: 7 },
    minPixels: { width: 360, height: 320 },
    Render: PerformanceOverviewWidget,
  },
  'my-tasks': {
    type: 'my-tasks',
    label: 'Meine Aufgaben',
    description: 'Offene Aufgaben, die dir zugewiesen sind — nach Fälligkeit sortiert.',
    icon: ListChecks,
    defaultSize: { w: 3, h: 8 },
    minSize: { w: 2, h: 5 },
    minPixels: { width: 180, height: 220 },
    Render: MyTasksWidget,
  },
  'team-status': {
    type: 'team-status',
    label: 'Team-Status',
    description: 'Wer ist heute abwesend, aktiv oder verfügbar — auf einen Blick.',
    icon: Activity,
    defaultSize: { w: 3, h: 8 },
    minSize: { w: 2, h: 5 },
    minPixels: { width: 180, height: 240 },
    Render: TeamStatusWidget,
  },
  'range-report': {
    type: 'range-report',
    label: 'Zeitraum-Report',
    description: 'Vollständiger Zeitraum-Report mit Filtern, Charts und byProject-Tabelle.',
    icon: BarChart3,
    // Embeds the full /auswertung body — needs more room than a typical
    // compact widget. Width 6 columns + height 10 rows fits the date
    // filters, project select and chart comfortably; the user can resize.
    defaultSize: { w: 6, h: 10 },
    minSize: { w: 4, h: 7 },
    minPixels: { width: 300, height: 260 },
    Render: RangeReportWidget,
  },
}

export function widgetDef(type: WidgetType): WidgetDef | null {
  return WIDGETS[type] ?? null
}

export function listWidgets(): WidgetDef[] {
  return Object.values(WIDGETS)
}
